import { Tooltip, CompanyLogo, CustomerAvatar } from '@cycle-app/ui';
import { DownIcon } from '@cycle-app/ui/icons';
import { ReactNode } from 'react';

import { DocCustomer } from 'src/components/DocCustomer/DocCustomer';
import DropdownLayer from 'src/components/DropdownLayer/DropdownLayer';
import { useCompanyModal } from 'src/hooks/modals/useCompanyModal';
import { useCustomerModal } from 'src/hooks/modals/useCustomerModal';
import useOptimizedBooleanState from 'src/hooks/useOptimizedBooleanState';
import { Layer } from 'src/types/layers.types';

import {
  Trigger, NoCustomerIcon, Label, Text, CompanyLabel, CustomerLabel,
} from './DocCompanyCustomer.styles';
import { DocCompanyCustomerModalContent } from './DocCompanyCustomerModalContent';

import type { DocCompanyCustomerProps } from './DocCompanyCustomer.types';

export const DocCompanyCustomer = ({
  canRemoveCustomer = false,
  doc,
  isDisabled = false,
  isCompact = false,
  isReadOnly = isDisabled,
  showArrow = false,
  showCompanyName,
  size,
  children,
}: DocCompanyCustomerProps) => {
  const [showChangeCustomer, {
    setTrueCallback, setFalseCallback,
  }] = useOptimizedBooleanState(false);

  if (!doc) return null;

  const arrow = showArrow ? (
    <DownIcon
      size={14}
      direction={showChangeCustomer ? 'top' : 'bottom'}
      style={{ marginLeft: 8 }}
    />
  ) : null;

  const getContent = () => {
    if (doc.customer?.company?.id) {
      return (
        <>
          <CompanyLogo company={doc.customer.company} size={size === 'S' ? 'XS' : 'S'} />
          {!isCompact && <CustomerLabel $size={size}>{doc.customer.displayName}</CustomerLabel>}
          {!isCompact && showCompanyName && (
            <>
              <Text $size={size}>from</Text>
              <CompanyLabel $size={size}>
                {doc.customer.company.name}
              </CompanyLabel>
            </>
          )}
          {arrow}
        </>
      );
    }
    if (doc.customer?.id) {
      return (
        <>
          <CustomerAvatar customer={doc.customer} size="S" />
          {!isCompact && <CustomerLabel $size={size}>{doc.customer.displayName}</CustomerLabel>}
          {arrow}
        </>
      );
    }
    if (isReadOnly) {
      return (
        <>
          <NoCustomerIcon onClick={isReadOnly ? undefined : setTrueCallback} />
          {!isCompact && <CustomerLabel $size={size} $muted>No customer</CustomerLabel>}
        </>
      );
    }
    return (
      <Tooltip content="Add customer" placement="top">
        <NoCustomerIcon onClick={setTrueCallback} />
        {!isCompact && <Label $size={size} $muted>Add customer</Label>}
        {arrow}
      </Tooltip>
    );
  };

  if (doc.customer && !showChangeCustomer) {
    return (
      <HasCustomer
        doc={doc}
        onClick={isReadOnly ? undefined : setTrueCallback}
        isReadOnly={isReadOnly}
      >
        {children ?? getContent()}
      </HasCustomer>
    );
  }

  return (
    <NoCustomer
      doc={doc}
      forceOpen={showChangeCustomer}
      onCloseChange={setFalseCallback}
      canRemoveCustomer={canRemoveCustomer}
    >
      {children ?? getContent()}
    </NoCustomer>
  );
};

type HasCustomerProps = {
  doc: DocCompanyCustomerProps['doc'];
  children: ReactNode;
  onClick?: VoidFunction;
  isReadOnly: boolean;
};

const HasCustomer = ({
  doc, children, onClick, isReadOnly,
}: HasCustomerProps) => {
  const { isOpen: isCustomerOpened } = useCustomerModal();
  const { isOpen: isCompanyOpened } = useCompanyModal();
  return (
    <DropdownLayer
      placement="top"
      animation={false}
      layer={Layer.DropdownModalZ3}
      delay={[400, 0]}
      content={isCustomerOpened || isCompanyOpened ? null : (
        <DocCompanyCustomerModalContent
          doc={doc}
          isReadOnly={isReadOnly}
          onClickUpdate={onClick}
        />
      )}
    >
      <Trigger
        $isReadOnly={isReadOnly}
        onClick={(e) => {
          if (isReadOnly) return;
          e.preventDefault();
          e.stopPropagation();
          onClick?.();
        }}
      >
        {children}
      </Trigger>
    </DropdownLayer>
  );
};

type NoCustomerProps = {
  doc: DocCompanyCustomerProps['doc'];
  children: ReactNode;
  forceOpen: boolean;
  canRemoveCustomer: boolean;
  onCloseChange: VoidFunction;
};

const NoCustomer = ({
  doc, children, forceOpen = false, onCloseChange, canRemoveCustomer,
}: NoCustomerProps) => {
  return (
    <DocCustomer
      doc={doc}
      forceOpenEditCustomer={forceOpen}
      onCloseChange={onCloseChange}
      isRemovable={canRemoveCustomer}
      isCompact
      context="doc-item"
    >
      {children}
    </DocCustomer>
  );
};
